interface ParsedRepo {
    url: string
    host: string
    owner: string
    repo: string
}

const supportedHosts = ['github.com', 'gitlab.com', 'bitbucket.org']

/**
 * Parse and normalize a repo URL.
 * Accepts: full HTTPS URLs (GitHub/GitLab/Bitbucket), with/without .git,
 * and owner/repo shorthand (assumes GitHub).
 */
export const parseRepoUrl = (input: string): ParsedRepo => {
    const trimmed = input.trim()
    if (!trimmed) {
        throw new Error('Repository URL is empty')
    }

    // owner/repo shorthand (no dots, no slashes except one)
    if (/^[a-zA-Z0-9_.-]+\/[a-zA-Z0-9_.-]+$/.test(trimmed)) {
        const [owner, repo] = trimmed.split('/')
        return {
            url: `https://github.com/${owner.toLowerCase()}/${repo.toLowerCase()}`,
            host: 'github.com',
            owner: owner.toLowerCase(),
            repo: repo.toLowerCase(),
        }
    }

    // A host without a protocol, like github.com/owner/repo
    const withProtocol = /^[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+\//.test(trimmed) ? `https://${trimmed}` : trimmed

    let url: URL
    try {
        url = new URL(withProtocol)
    } catch {
        throw new Error(`Invalid repository URL: ${trimmed}`)
    }

    if (url.protocol !== 'https:' && url.protocol !== 'http:') {
        throw new Error(`Unsupported protocol: ${url.protocol}`)
    }

    const host = url.hostname.toLowerCase().replace(/^www\./, '')
    if (!supportedHosts.includes(host)) {
        throw new Error(`Unsupported host: ${host}. Supported: ${supportedHosts.join(', ')}`)
    }

    // Strip .git suffix and normalize path
    let path = url.pathname.replace(/\.git$/, '').replace(/\/+$/, '')
    // Remove leading slash
    path = path.replace(/^\//, '')

    const segments = path.split('/').filter(Boolean)
    if (segments.length < 2) {
        throw new Error('URL must contain owner and repository name')
    }

    const owner = segments[0].toLowerCase()
    const repo = segments[1].toLowerCase()

    return {
        url: `https://${host}/${owner}/${repo}`,
        host,
        owner,
        repo,
    }
}

export const repoToDir = (parsed: ParsedRepo): string => {
    return `/${parsed.host}/${parsed.owner}/${parsed.repo}`
}

/**
 * Extract a repo from a URL pathname like /github.com/owner/repo, or /owner/repo (assumes GitHub).
 * Returns null if invalid.
 */
export const parseRepoFromPathname = (pathname: string): ParsedRepo | null => {
    const trimmed = pathname.replace(/^\//, '')
    if (!trimmed) return null
    const segments = trimmed.split('/').filter(Boolean)
    const hasHost = segments[0].includes('.')
    if (!hasHost && segments.length !== 2) return null
    try {
        return parseRepoUrl(`https://${hasHost ? trimmed : `github.com/${trimmed}`}`)
    } catch {
        return null
    }
}

/** LightningFS IndexedDB database name for a parsed repo. */
export const repoToFsName = (parsed: ParsedRepo): string => {
    return `git-strata-${parsed.host}-${parsed.owner}-${parsed.repo}`
}

/** LightningFS IndexedDB database name from a normalized repo URL (like https://github.com/owner/repo). */
export const repoUrlToFsName = (repoUrl: string): string => {
    return repoToFsName(parseRepoUrl(repoUrl))
}
